import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Radio, Gem, Wallet, ReceiptText, LineChart, Users, Shield,
  Sparkles, BellRing, Cog, Plug, Store as StoreIcon, Megaphone, Menu, X, ChevronRight, Search,
  Wand2,
} from "lucide-react";
import { useAuth } from "@/lib/auth";

import Overview from "./sections/Overview";
import Channel from "./sections/Channel";
import Subscriptions from "./sections/Subscriptions";
import Payments from "./sections/Payments";
import Tools from "./sections/Tools";
import Transactions from "./sections/Transactions";
import Community from "./sections/Community";
import Moderation from "./sections/Moderation";
import Preferences from "./sections/Preferences";
import Integrations from "./sections/Integrations";
import Analytics from "./sections/Analytics";
import Alerts from "./sections/Alerts";
import Emotes from "./sections/Emotes";
import Promotions from "./sections/Promotions";
import Store from "./sections/Store";
import ComingSoonSection from "./sections/ComingSoonSection";

// ---------------------------------------------------------------------------
// Nav model. Keep this the SOLE source of truth so adding a section later is
// a one-line edit here + a new sections/ file. `ready:false` renders a
// polished placeholder so the surface area is visible from day one without
// half-built features shipping to streamers.
// ---------------------------------------------------------------------------
export const DASHBOARD_SECTIONS = [
  { slug: "overview",      label: "Overview",       icon: LayoutDashboard, group: "Home",       ready: true,  color: "volt",   component: Overview,     desc: "Live status, revenue snapshot, quick actions" },
  { slug: "channel",       label: "Channel",        icon: Radio,           group: "Studio",     ready: true,  color: "cyan",   component: Channel,      desc: "Profile, banner, categories, tags, socials" },
  { slug: "subscriptions", label: "Subscriptions",  icon: Gem,             group: "Studio",     ready: true,  color: "pink",   component: Subscriptions, desc: "Pricing, sub-only chat, sub emotes" },
  { slug: "payments",      label: "Payments",       icon: Wallet,          group: "Money",      ready: true,  color: "volt",   component: Payments,     desc: "Revenue, payouts, Stripe Connect" },
  { slug: "transactions",  label: "Transactions",   icon: ReceiptText,     group: "Money",      ready: true,  color: "cyan",   component: Transactions, desc: "Full ledger, filters, CSV export" },
  { slug: "analytics",     label: "Analytics",      icon: LineChart,       group: "Growth",     ready: true,  color: "pink",   component: Analytics,    desc: "Viewer, revenue, engagement charts" },
  { slug: "community",     label: "Community",      icon: Users,           group: "Audience",   ready: true,  color: "cyan",   component: Community,    desc: "Followers, subscribers, mods, bans" },
  { slug: "moderation",    label: "Moderation",     icon: Shield,          group: "Audience",   ready: true,  color: "orange", component: Moderation,   desc: "AutoMod, chat rules, mod logs" },
  { slug: "emotes",        label: "Emotes",         icon: Sparkles,        group: "Audience",   ready: true,  color: "pink",   component: Emotes,       desc: "Upload custom channel emotes" },
  { slug: "alerts",        label: "Alerts",         icon: BellRing,        group: "Audience",   ready: true,  color: "volt",   component: Alerts,       desc: "Follower/sub/tip alerts + OBS overlay" },
  { slug: "preferences",   label: "Preferences",    icon: Cog,             group: "Account",    ready: true,  color: "cyan",   component: Preferences,  desc: "Security, notifications, KYC" },
  { slug: "integrations",  label: "Integrations",   icon: Plug,            group: "Account",    ready: true,  color: "volt",   component: Integrations, desc: "OBS, Cloudflare, Discord, YouTube" },
  { slug: "promotions",    label: "Promotions",     icon: Megaphone,       group: "Grow",       ready: true,  color: "orange", component: Promotions,   desc: "Referrals, boosts, featured applications" },
  { slug: "store",         label: "Store",          icon: StoreIcon,       group: "Grow",       ready: true,  color: "pink",   component: Store,        desc: "External merch shop links + product previews" },
  { slug: "tools",         label: "Creator Tools",  icon: Wand2,           group: "Grow",       ready: true,  color: "cyan",   component: Tools,        desc: "Stream goals & live polls" },
];

// Group ordering.
export const GROUP_ORDER = ["Home", "Studio", "Money", "Growth", "Audience", "Account", "Grow"];

// ---------------------------------------------------------------------------
// Unsaved-changes context — sections register a boolean; the shell warns on
// navigation. Any section can call `useUnsaved(dirty)` inside its component.
// ---------------------------------------------------------------------------
const UnsavedCtx = createContext({ dirty: false, setDirty: () => {} });

export function useUnsaved(dirty) {
  const ctx = useContext(UnsavedCtx);
  useEffect(() => {
    ctx.setDirty(!!dirty);
    return () => ctx.setDirty(false);
  }, [dirty]);
}

// Browser tab-close guard.
function useBeforeUnloadGuard(active) {
  useEffect(() => {
    if (!active) return;
    const fn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", fn);
    return () => window.removeEventListener("beforeunload", fn);
  }, [active]);
}

// ---------------------------------------------------------------------------
// Main shell
// ---------------------------------------------------------------------------
export default function DashboardShell() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [dirty, setDirty] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState("");

  useBeforeUnloadGuard(dirty);

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [loading, user, nav]);

  // Close mobile drawer on route change.
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  // Guarded navigation — used by the sidebar links when there are unsaved edits.
  const guardedGo = (to) => {
    if (dirty) {
      const ok = window.confirm("You have unsaved changes. Leave without saving?");
      if (!ok) return;
      setDirty(false);
    }
    nav(to);
  };

  const grouped = useMemo(() => {
    const filter = search.trim().toLowerCase();
    const items = filter
      ? DASHBOARD_SECTIONS.filter(
          (s) => s.label.toLowerCase().includes(filter) || (s.desc || "").toLowerCase().includes(filter)
        )
      : DASHBOARD_SECTIONS;
    const byGroup = {};
    items.forEach((s) => {
      byGroup[s.group] = byGroup[s.group] || [];
      byGroup[s.group].push(s);
    });
    return GROUP_ORDER.filter((g) => byGroup[g]).map((g) => [g, byGroup[g]]);
  }, [search]);

  const currentSlug = loc.pathname.split("/")[2] || "overview";
  const currentSection = DASHBOARD_SECTIONS.find((s) => s.slug === currentSlug) || DASHBOARD_SECTIONS[0];

  if (loading || !user) return null;

  return (
    <UnsavedCtx.Provider value={{ dirty, setDirty }}>
      <div className="min-h-screen bg-[#0A0A0C]" data-testid="dashboard-shell">
        <div className="flex">
          {/* -------- Sidebar (desktop) -------- */}
          <aside
            className="hidden lg:flex flex-col w-64 xl:w-72 shrink-0 border-r border-white/5 bg-[#0B0B10] sticky top-0 h-screen"
            data-testid="dashboard-sidebar"
          >
            <SidebarInner
              user={user}
              search={search}
              setSearch={setSearch}
              grouped={grouped}
              guardedGo={guardedGo}
              currentSlug={currentSlug}
            />
          </aside>

          {/* -------- Mobile drawer -------- */}
          {mobileOpen && (
            <div className="fixed inset-0 z-50 lg:hidden" data-testid="dashboard-mobile-drawer">
              <div
                className="absolute inset-0 bg-black/70 backdrop-blur-sm"
                onClick={() => setMobileOpen(false)}
              />
              <aside className="relative w-72 max-w-[85vw] h-full bg-[#0B0B10] border-r border-white/10 overflow-y-auto">
                <div className="flex items-center justify-between p-4 border-b border-white/10">
                  <span className="sc-heading text-lg font-black">Menu</span>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="p-2 rounded hover:bg-white/5"
                    data-testid="dashboard-mobile-close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <SidebarInner
                  user={user}
                  search={search}
                  setSearch={setSearch}
                  grouped={grouped}
                  guardedGo={(to) => { setMobileOpen(false); guardedGo(to); }}
                  currentSlug={currentSlug}
                />
              </aside>
            </div>
          )}

          {/* -------- Main content -------- */}
          <main className="flex-1 min-w-0">
            {/* Mobile top bar */}
            <div className="lg:hidden flex items-center justify-between px-4 py-3 border-b border-white/5 sticky top-0 z-30 bg-[#0A0A0C]/95 backdrop-blur">
              <button
                onClick={() => setMobileOpen(true)}
                className="p-2 rounded hover:bg-white/5"
                data-testid="dashboard-mobile-open"
              >
                <Menu className="w-5 h-5" />
              </button>
              <div className="flex items-center gap-2">
                <currentSection.icon className="w-4 h-4" style={{ color: `var(--sc-${currentSection.color})` }} />
                <span className="sc-heading font-black text-sm">{currentSection.label}</span>
              </div>
              <div className="w-9" />
            </div>

            {/* Breadcrumb + section header (desktop) */}
            <div className="hidden lg:block px-8 xl:px-10 pt-6 pb-2">
              <div className="flex items-center gap-2 text-[10px] sc-mono text-zinc-500" data-testid="dashboard-breadcrumb">
                <span>DASHBOARD</span>
                <ChevronRight className="w-3 h-3" />
                <span style={{ color: `var(--sc-${currentSection.color})` }}>{currentSection.label.toUpperCase()}</span>
                {dirty && (
                  <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold bg-[color:var(--sc-orange)]/15 text-[color:var(--sc-orange)]" data-testid="dashboard-dirty-indicator">
                    UNSAVED CHANGES
                  </span>
                )}
              </div>
              <div className="flex items-start justify-between gap-4 mt-2">
                <div>
                  <h1 className="sc-heading text-3xl xl:text-4xl font-black tracking-tight">{currentSection.label}</h1>
                  <p className="text-sm text-zinc-500 mt-1">{currentSection.desc}</p>
                </div>
              </div>
            </div>

            {/* Routed section */}
            <div className="px-4 sm:px-6 lg:px-8 xl:px-10 py-6 pb-24" data-testid="dashboard-section-content">
              <Routes>
                <Route index element={<Navigate to="overview" replace />} />
                {DASHBOARD_SECTIONS.map((s) => (
                  <Route
                    key={s.slug}
                    path={s.slug}
                    element={
                      s.ready && s.component
                        ? <s.component />
                        : <ComingSoonSection section={s} />
                    }
                  />
                ))}
                <Route path="*" element={<Navigate to="overview" replace />} />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </UnsavedCtx.Provider>
  );
}

// ---------------------------------------------------------------------------
// Sidebar contents (shared between desktop + mobile drawer)
// ---------------------------------------------------------------------------
function SidebarInner({ user, search, setSearch, grouped, guardedGo, currentSlug }) {
  return (
    <>
      <div className="p-5 border-b border-white/5">
        <div className="sc-label" style={{ color: "var(--sc-volt)" }}>◤ CREATOR STUDIO</div>
        <div className="mt-2 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[color:var(--sc-volt)] to-[color:var(--sc-cyan)] grid place-items-center text-black font-black">
            {user.username[0]?.toUpperCase()}
          </div>
          <div className="min-w-0">
            <div className="sc-heading text-sm font-black truncate">{user.username}</div>
            <div className="text-[10px] sc-mono text-zinc-500 capitalize">{user.tier || "free"} · {user.is_verified_streamer ? "verified" : "unverified"}</div>
          </div>
        </div>
      </div>

      <div className="p-3 border-b border-white/5">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search settings…"
            className="w-full pl-8 pr-2 py-2 bg-[#0F0F14] border border-white/10 focus:border-[color:var(--sc-cyan)] focus:outline-none rounded sc-mono text-xs text-zinc-100 placeholder:text-zinc-600"
            data-testid="dashboard-search-input"
          />
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-3">
        {grouped.length === 0 ? (
          <div className="text-center text-xs text-zinc-500 py-8">No settings match &ldquo;{search}&rdquo;</div>
        ) : (
          grouped.map(([group, items]) => (
            <div key={group} className="mb-4">
              <div className="px-4 mb-1 sc-label text-[9px] tracking-widest text-zinc-500">{group}</div>
              <div className="space-y-0.5">
                {items.map((s) => (
                  <SidebarItem
                    key={s.slug}
                    section={s}
                    active={currentSlug === s.slug}
                    onClick={() => guardedGo(`/dashboard/${s.slug}`)}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </nav>

      <div className="p-3 border-t border-white/5">
        <NavLink
          to={`/c/${user.username}`}
          className="block text-center text-[10px] sc-mono text-zinc-500 hover:text-[color:var(--sc-cyan)] transition"
          data-testid="dashboard-view-channel-link"
        >
          VIEW PUBLIC CHANNEL →
        </NavLink>
      </div>
    </>
  );
}

function SidebarItem({ section, active, onClick }) {
  const Icon = section.icon;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`dashboard-nav-${section.slug}`}
      className={`w-full text-left flex items-center gap-3 px-4 py-2 transition group ${
        active
          ? "bg-white/5 border-l-2"
          : "border-l-2 border-transparent hover:bg-white/[0.03]"
      }`}
      style={active ? { borderColor: `var(--sc-${section.color})` } : undefined}
    >
      <Icon
        className="w-4 h-4 shrink-0"
        style={{ color: active ? `var(--sc-${section.color})` : "rgb(161 161 170)" }}
      />
      <span
        className={`text-sm truncate ${active ? "font-bold text-zinc-100" : "text-zinc-400 group-hover:text-zinc-200"}`}
      >
        {section.label}
      </span>
      {!section.ready && (
        <span className="ml-auto text-[9px] sc-mono px-1 py-0.5 rounded bg-white/5 text-zinc-500">SOON</span>
      )}
    </button>
  );
}
